// ═══════════════════════════════════════════════════════════════════════
// Compte IPM du salarié (lot UX-1, 3 octobre 2026).
// Lecture comme un relevé de compte : solde d'ouverture, mouvements, solde courant, solde de
// clôture. Libellés neutres : ni soin, ni prestataire, ni prénom d'ayant droit. Relevé PDF
// provisoire (consultation, non conservé) ou arrêté (référencé, conservé, versionné).
// Socle visuel SOGAS RH : icônes lucide, aucun emoji, pagination de 10 lignes.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, Download, History, Wallet, Receipt, X, Check, ShieldAlert } from 'lucide-react';
import Layout from '../components/Layout';
import { ouvrirPdf, messageErreurBlob as erreurBlob } from '../utils/ipmPdf';
import { useAuth } from '../context/AuthContext';
import {
  EnTetePage, Bouton, Carte, Etat, BandeauInfo, Onglets, EtatVide, Squelette, Pagination, Champ, Grille, formaterDate, NON_RENSEIGNE,
} from '../design/composants';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const fmtF = (n) => (n === null || n === undefined ? NON_RENSEIGNE : `${Number(n).toLocaleString('fr-FR')} FCFA`);
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const debutAnnee = () => `${new Date().getFullYear()}-01-01`;
const messageErreur = (e, d = 'L\'opération n\'a pas abouti.') => e?.response?.data?.message || d;
const LIBELLE_RETENUE = { PROPOSEE: 'Proposée', ARRETEE: 'Arrêtée, en attente de la paie', RETOURNEE: 'Retenue par la paie', ANNULEE: 'Annulée' };
const TON_RETENUE = { PROPOSEE: 'info', ARRETEE: 'alerte', RETOURNEE: 'succes', ANNULEE: 'neutre' };

function FenetreReleve({ compte, token, onFermer, onArrete }) {
  const [f, setF] = useState({ debut: compte.periode.debut, fin: compte.periode.fin, nature: 'provisoire', destinataire: '', visa: false, motif_reedition: '' });
  const [erreur, setErreur] = useState(null);
  const [reedition, setReedition] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);
  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', echap);
    return () => document.removeEventListener('keydown', echap);
  }, [onFermer]);
  const generer = async () => {
    const onglet = window.open('', '_blank'); // ouvert pendant le clic
    setEnvoi(true); setErreur(null);
    try {
      if (f.nature === 'provisoire') {
        await ouvrirPdf(token, `/ipm/comptes/${compte.employe.id}/releve.pdf`, { debut: f.debut, fin: f.fin }, onglet);
        onFermer();
      } else {
        let r;
        try {
          r = await axios.post(`${API}/ipm/comptes/${compte.employe.id}/releves`, {
            debut: f.debut, fin: f.fin, destinataire: f.destinataire || undefined, visa: f.visa, motif_reedition: reedition ? f.motif_reedition : undefined,
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (er) { if (onglet) onglet.close(); throw er; }
        setResultat(r.data);
        await ouvrirPdf(token, `/ipm/documents/${r.data.document.id}/fichier`, undefined, onglet);
        onArrete && onArrete();
      }
    } catch (e) {
      // Réédition d'un relevé arrêté : proposée seulement à la supervision IPM (droits.reedition).
      if (e?.response?.data?.code === 'REEDITION_MOTIF_OBLIGATOIRE' && compte.droits.reedition) setReedition(e.response.data.details);
      setErreur(e?.response?.data instanceof Blob ? await erreurBlob(e) : messageErreur(e));
    } finally { setEnvoi(false); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Relevé de compte IPM"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="rh-carte" style={{ width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
        <div className="rh-carte-entete">
          <h2 className="rh-carte-titre">Relevé de compte IPM</h2>
          <Bouton variante="discret" className="rh-btn-icone" Icone={X} onClick={onFermer} aria-label="Fermer" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 13 }}>Du<input className="rh-champ-saisie" type="date" value={f.debut} max={f.fin} onChange={(e) => setF({ ...f, debut: e.target.value })} /></label>
          <label style={{ fontSize: 13 }}>Au<input className="rh-champ-saisie" type="date" value={f.fin} max={aujourdhui()} onChange={(e) => setF({ ...f, fin: e.target.value })} /></label>
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: '14px 0' }}>
          <legend style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Nature du document</legend>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, fontSize: 13 }}>
            <input type="radio" name="nature" checked={f.nature === 'provisoire'} onChange={() => setF({ ...f, nature: 'provisoire' })} />
            <span><strong>Provisoire</strong> — consultation à l'écran ou impression de travail. Non conservé : seule la trace de la consultation est gardée.</span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, opacity: compte.droits.releve_arrete ? 1 : 0.5 }}>
            <input type="radio" name="nature" disabled={!compte.droits.releve_arrete} checked={f.nature === 'arrete'} onChange={() => setF({ ...f, nature: 'arrete' })} />
            <span><strong>Arrêté</strong> — document remis ou officiel. Référencé, conservé 10 ans, jamais dupliqué ; une réédition crée une nouvelle version{compte.droits.reedition ? '' : ', réservée au Gestionnaire IPM'}.{!compte.droits.releve_arrete && (compte.droits.propre_dossier ? ' (impossible sur votre propre dossier IPM)' : ' (réservé à la gestion IPM)')}</span>
          </label>
        </fieldset>
        {f.nature === 'arrete' && (
          <>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>Remis à (facultatif)
              <input className="rh-champ-saisie" value={f.destinataire} placeholder="Ex. : remis au participant au guichet" onChange={(e) => setF({ ...f, destinataire: e.target.value })} />
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 10 }}>
              <input type="checkbox" checked={f.visa} onChange={(e) => setF({ ...f, visa: e.target.checked })} /> Ajouter une zone de visa et de cachet
            </label>
            {reedition && (
              <>
                <BandeauInfo ton="alerte" titre={`Un relevé arrêté existe déjà (réf. ${reedition.reference} v${reedition.version})`}>
                  Les données ont changé depuis. Une nouvelle version le remplacera ; l'ancienne restera consultable.
                </BandeauInfo>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>Motif de la réédition<span className="rh-form-requis" aria-hidden="true">*</span>
                  <textarea className="rh-champ-saisie" rows={2} value={f.motif_reedition} onChange={(e) => setF({ ...f, motif_reedition: e.target.value })} />
                </label>
              </>
            )}
          </>
        )}
        {erreur && !reedition && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {resultat && <BandeauInfo ton="succes">{resultat.message}</BandeauInfo>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <Bouton variante="discret" onClick={onFermer}>Fermer</Bouton>
          <Bouton variante="primaire" Icone={Check} disabled={envoi || (reedition && f.motif_reedition.trim().length < 5)} onClick={generer}>
            {envoi ? 'Génération…' : f.nature === 'provisoire' ? 'Afficher le relevé provisoire' : reedition ? 'Rééditer (nouvelle version)' : 'Arrêter et générer'}
          </Bouton>
        </div>
      </div>
    </div>
  );
}

export default function IpmComptePage() {
  const { employeId } = useParams();
  const { token } = useAuth();
  const [periode, setPeriode] = useState({ debut: debutAnnee(), fin: aujourdhui() });
  const [compte, setCompte] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState('compte');
  const [page, setPage] = useState(1);
  const [fenetre, setFenetre] = useState(false);
  const charger = useCallback(() => {
    setErreur(null);
    axios.get(`${API}/ipm/comptes/${employeId}`, { params: periode, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setCompte(r.data); setPage(1); })
      .catch((e) => setErreur(messageErreur(e)));
  }, [employeId, periode, token]);
  useEffect(() => { charger(); }, [charger]);

  // Lignes du relevé à l'écran : ouverture, mouvements, clôture (pagination de 10).
  const lignes = useMemo(() => (compte ? compte.mouvements : []), [compte]);
  const visibles = lignes.slice((page - 1) * 10, page * 10);
  const nom = compte ? `${compte.employe.prenom || ''} ${(compte.employe.nom || '').toUpperCase()}`.trim() : '';
  const e = compte?.entete;
  const ongletsVisibles = [
    { cle: 'compte', libelle: 'Compte', Icone: Wallet },
    { cle: 'retenues', libelle: 'Retenues', Icone: Receipt, compte: compte?.retenues?.length },
    ...(compte?.documents ? [{ cle: 'documents', libelle: 'Documents', Icone: FileText, compte: compte.documents.total }] : []),
  ];

  return (
    <Layout>
      <div className="rh-page rh-page-large">
        <EnTetePage
          fil={[{ libelle: 'IPM', vers: '/ipm' }, { libelle: 'Comptes' }, { libelle: nom || 'Compte' }]}
          titre={compte ? `Compte IPM — ${nom}` : 'Compte IPM'}
          sousTitre={compte ? `${compte.employe.matricule}${compte.employe.site ? ` · ${compte.employe.site}` : ''} · ${compte.employe.type_contrat || ''} · ${compte.employe.statut}` : null}
          actions={compte?.droits?.releve_provisoire && <Bouton variante="primaire" Icone={FileText} onClick={() => setFenetre(true)}>Relevé PDF</Bouton>}
        />
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {!compte && !erreur && <Squelette hauteur={220} />}
        {compte && (
          <>
            {compte.droits?.propre_dossier && <BandeauInfo ton="alerte">Votre propre dossier IPM : consultation seulement. Une autre personne habilitée doit le traiter.</BandeauInfo>}
            <Carte>
              <Grille>
                <Champ libelle={`Cotisation du mois (${e.periode})`} valeur={e.cotisation_mois ? `${fmtF(e.cotisation_mois.montant)} · ${e.cotisation_mois.statut === 'preleve' ? 'constatée par la paie' : 'en attente de la paie'}` : 'Aucune cotisation générée'} />
                <Champ libelle="Dette IPM actuelle" valeur={fmtF(e.dette_actuelle)} />
                <Champ libelle="Retenue de la période" valeur={e.retenue_periode
                  ? `${fmtF(e.retenue_periode.retenu ?? e.retenue_periode.applicable ?? e.retenue_periode.propose)} · ${LIBELLE_RETENUE[e.retenue_periode.statut]}`
                  : 'Aucune retenue prévue'} />
                <Champ libelle="Reste après retenue" valeur={fmtF(e.reste_apres_retenue)} />
              </Grille>
              <p className="rh-cellule-secondaire" style={{ marginTop: 8 }}>
                Dette IPM = somme des restes à charge du salarié, diminuée des sommes récupérées. Aucune donnée médicale n'est affichée ici.
              </p>
            </Carte>

            <Onglets onglets={ongletsVisibles} actif={onglet} onChange={setOnglet} libelle="Compte IPM" />
            <div role="tabpanel" id={`panneau-${onglet}`} aria-labelledby={`onglet-${onglet}`} style={{ marginTop: 16 }}>
              {onglet === 'compte' && (
                <Carte titre="Mouvements" Icone={History} action={(
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 13 }}>Du <input type="date" className="rh-champ-saisie" style={{ width: 'auto' }} value={periode.debut} max={periode.fin} onChange={(ev) => setPeriode({ ...periode, debut: ev.target.value })} /></label>
                    <label style={{ fontSize: 13 }}>au <input type="date" className="rh-champ-saisie" style={{ width: 'auto' }} value={periode.fin} max={aujourdhui()} onChange={(ev) => setPeriode({ ...periode, fin: ev.target.value })} /></label>
                  </div>
                )}>
                  <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
                    <table className="rh-tableau">
                      <thead><tr><th>Date</th><th>Référence</th><th>Opération</th><th style={{ textAlign: 'right' }}>Débit</th><th style={{ textAlign: 'right' }}>Crédit</th><th style={{ textAlign: 'right' }}>Solde</th></tr></thead>
                      <tbody>
                        <tr className="rh-ligne-inactive"><td>{formaterDate(compte.periode.debut)}</td><td /><td className="rh-cellule-principale">Solde d'ouverture</td><td /><td /><td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtF(compte.solde_ouverture)}</td></tr>
                        {visibles.map((m) => (
                          <tr key={m.id} className="rh-ligne-inactive">
                            <td>{formaterDate(m.date)}</td>
                            <td className="rh-cellule-secondaire">{m.reference || ''}</td>
                            <td>{m.libelle}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{m.debit ? `+ ${fmtF(m.debit)}` : ''}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{m.credit ? `− ${fmtF(m.credit)}` : ''}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(m.solde)}</td>
                          </tr>
                        ))}
                        {lignes.length === 0 && <tr className="rh-ligne-inactive"><td colSpan={6} className="rh-cellule-secondaire">Aucun mouvement sur la période.</td></tr>}
                        <tr className="rh-ligne-inactive"><td>{formaterDate(compte.periode.fin)}</td><td /><td className="rh-cellule-principale">Solde de clôture</td><td /><td /><td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtF(compte.solde_cloture)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <Pagination page={page} pages={Math.max(1, Math.ceil(lignes.length / 10))} total={lignes.length} taille={10} onPage={setPage} />
                  <p className="rh-cellule-secondaire" style={{ marginTop: 6 }}>Seuls les mouvements validés figurent ici. Débit : somme due (reste à charge). Crédit : somme récupérée ou annulée.</p>
                </Carte>
              )}

              {onglet === 'retenues' && (
                <Carte titre="Retenues mensuelles" Icone={Receipt}>
                  {compte.retenues.length === 0 ? <EtatVide titre="Aucune retenue" /> : (
                    <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
                      <table className="rh-tableau">
                        <thead><tr><th>Période</th><th style={{ textAlign: 'right' }}>Proposée</th><th style={{ textAlign: 'right' }}>Applicable</th><th style={{ textAlign: 'right' }}>Retenue</th><th>État</th><th>Écart</th></tr></thead>
                        <tbody>
                          {compte.retenues.slice(0, 10).map((r) => (
                            <tr key={r.id} className="rh-ligne-inactive">
                              <td>{r.periode}</td>
                              <td style={{ textAlign: 'right' }}>{fmtF(r.montant_propose)}</td>
                              <td style={{ textAlign: 'right' }}>{r.montant_applicable === null ? '—' : fmtF(r.montant_applicable)}</td>
                              <td style={{ textAlign: 'right' }}>{r.montant_retenu === null ? '—' : fmtF(r.montant_retenu)}</td>
                              <td><Etat ton={TON_RETENUE[r.statut]}>{LIBELLE_RETENUE[r.statut]}</Etat></td>
                              <td className="rh-cellule-secondaire">{r.motif_ecart || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Carte>
              )}

              {onglet === 'documents' && compte.documents && (
                <Carte titre="Documents arrêtés" Icone={FileText}>
                  {compte.documents.lignes.length === 0 ? <EtatVide titre="Aucun document arrêté" texte="Les relevés provisoires ne sont pas conservés ; seuls les documents arrêtés apparaissent ici." /> : (
                    <ul className="rh-liste">
                      {compte.documents.lignes.map((d) => (
                        <li key={d.id}>
                          <span style={{ flex: 1 }}>
                            <strong>{d.reference}</strong> v{d.version} · Relevé du {formaterDate(d.periode_debut)} au {formaterDate(d.periode_fin)}
                            <div className="rh-cellule-secondaire">Arrêté le {formaterDate(d.genere_le)} par {d.genere_par_nom} · {d.nb_remises || 0} remise(s){d.motif_reedition ? ` · réédition : ${d.motif_reedition}` : ''}</div>
                          </span>
                          <Etat ton={d.statut === 'ARRETE' ? 'succes' : 'neutre'}>{d.statut === 'ARRETE' ? 'En vigueur' : 'Remplacé'}</Etat>
                          <Bouton petit variante="discret" Icone={Download} onClick={async () => { try { await ouvrirPdf(token, `/ipm/documents/${d.id}/fichier`); } catch (er) { setErreur(await erreurBlob(er)); } }}>Ouvrir</Bouton>
                        </li>
                      ))}
                    </ul>
                  )}
                </Carte>
              )}
            </div>
          </>
        )}
        {!compte && erreur && (
          <EtatVide Icone={ShieldAlert} titre="Compte inaccessible" texte="Ce compte n'est accessible qu'avec une habilitation IPM attribuée explicitement." action={<Link to="/ipm" className="rh-btn">Retour à l'IPM</Link>} />
        )}
        {fenetre && compte && <FenetreReleve compte={compte} token={token} onFermer={() => setFenetre(false)} onArrete={charger} />}
      </div>
    </Layout>
  );
}
